import {
  type BlogList,
  type BlogMetadata,
  type CoreBlog,
  type FBlog,
  type IGetBreadcrumbRequest,
  type IGetBreadcrumbResponse,
  type RecursivePartial,
  SCHEMA_VERSION,
  type UniqueDocIdentifier,
  type UniqueIdentifier,
} from "@piperunner-cms/types/v1";
import {
  and,
  collection,
  type DocumentReference,
  type DocumentSnapshot,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { FIRESTORE_COLLECTION_PATH, NotFoundError } from "@/constants";
import {
  doesDocExist,
  doesDocumentBelongToUser,
  isDocumentIdentifierPresent,
  isUniqueIdentifierPresent,
  normalizeObject,
} from "@/utils";
import { fsDb } from "../utils/initialize-fs-db";
import { functions } from "../utils/initialize-functions";

// TODO: Remove all content and only get meta data list
// TODO: Use undefined for all blogs and null for blogs without parents
export const getBlogList = async (
  identifier: UniqueIdentifier & { parentBlogId?: string | null },
): Promise<BlogList> => {
  const { userId, projectId, parentBlogId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS);
  const docQuery =
    parentBlogId !== undefined
      ? query(
          collectionRef,
          and(
            where("userId", "==", userId),
            where("projectId", "==", projectId),
            where("parentDocumentId", "==", parentBlogId),
          ),
        )
      : query(collectionRef, and(where("userId", "==", userId), where("projectId", "==", projectId)));
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    throw new NotFoundError(`No resume found for userId: ${userId} and projectId: ${projectId}`);
  }

  const blogList = docSnap.docs.reduce<BlogList>((acc, doc) => {
    acc[doc.id] = {
      id: doc.id,
      payload: {
        slug: doc.data().slug,
        projectId: doc.data().projectId,
        userId: doc.data().userId,
        id: doc.data().id,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        publishedAt: doc.data().publishedAt,
        schemaVersion: doc.data().schemaVersion || 1,
        revision: doc.data().revision || 0,
        latest: doc.data().latest || null,
        draft: doc.data().draft || null,
        type: "blog",
        visibility: doc.data().visibility,
        parentDocumentId: doc.data().parentDocumentId || null,
        childDocumentIds: doc.data().childDocumentIds || null,
      },
    };
    return acc;
  }, {});

  return blogList;
};

export const getBlog = async (identifier: UniqueDocIdentifier): Promise<FBlog> => {
  const { userId, projectId, id } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);
  const docSnap = await getDoc(docRef);
  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  return {
    userId,
    projectId,
    schemaVersion: docSnap.data().schemaVersion,
    revision: docSnap.data().revision || 0,
    id: docSnap.id,
    slug: docSnap.data().slug,
    latest: docSnap.data().latest as CoreBlog | null,
    draft: docSnap.data().draft as CoreBlog | null,
    createdAt: docSnap.data().createdAt,
    updatedAt: docSnap.data().updatedAt,
    publishedAt: docSnap.data().publishedAt ?? null,
    type: "blog",
    visibility: docSnap.data().visibility,
    parentDocumentId: docSnap.data().parentDocumentId || null,
    childDocumentIds: docSnap.data().childDocumentIds || null,
  };
};

export const createBlog = async (
  identifier: UniqueIdentifier,
  metadata: Omit<BlogMetadata, "type" | "visibility">,
  blogData: Omit<CoreBlog, "content">,
  parentBlogId?: string | null,
): Promise<FBlog> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS);
  const docRef = doc(collectionRef);

  const _blogData: FBlog = {
    userId,
    projectId,
    id: docRef.id,
    slug: metadata.slug,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    publishedAt: null,
    latest: null,
    type: "blog",
    visibility: "public",
    parentDocumentId: parentBlogId || null,
    childDocumentIds: null,
    draft: {
      title: blogData.title,
      description: blogData.description,
      coverImage: blogData.coverImage || null,
      content: null,
      tagIds: blogData.tagIds || null,
      categoryId: blogData.categoryId || null,
    },
    schemaVersion: SCHEMA_VERSION,
    revision: 1,
  };

  await setDoc(docRef, _blogData);

  if (parentBlogId) {
    // Update parent blog to include this blog as a child
    const parentDocRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, parentBlogId);
    const parentDocSnap = await getDoc(parentDocRef);
    doesDocExist(parentDocSnap);
    doesDocumentBelongToUser({ userId, projectId }, parentDocSnap);

    const parentChildDocumentIds: string[] = parentDocSnap.data().childDocumentIds || [];
    parentChildDocumentIds.push(docRef.id);

    await setDoc(
      parentDocRef,
      {
        childDocumentIds: parentChildDocumentIds,
        updatedAt: new Date().toISOString(),
      },
      { merge: true },
    );
  }

  return _blogData;
};

export const deleteBlog = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  await runTransaction(fsDb, async (tx) => {
    const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);
    const docSnap = await tx.get(docRef);
    doesDocExist(docSnap);
    doesDocumentBelongToUser(identifier, docSnap);

    const childDocumentIds: string[] = docSnap.data().childDocumentIds || [];

    // Get reference to parent
    const parentDocumentId: string | null = docSnap.data().parentDocumentId || null;
    let parentDocRef: DocumentReference | null = null;
    let parentDocSnap: DocumentSnapshot | null = null;
    if (parentDocumentId) {
      parentDocRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, parentDocumentId);
      parentDocSnap = await tx.get(parentDocRef);
      doesDocExist(parentDocSnap);
      doesDocumentBelongToUser(identifier, parentDocSnap);
    }

    if (childDocumentIds.length > 0) {
      // recursively move children to parent of deleted blog
      const parentDocumentId: string | null = docSnap.data().parentDocumentId || null;

      const childDocSnaps = await Promise.all(
        childDocumentIds.map((childId) => {
          const childDocRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, childId);
          return tx.get(childDocRef);
        }),
      );

      for (const childDocSnap of childDocSnaps) {
        const childId = childDocSnap.id;
        doesDocExist(childDocSnap);
        doesDocumentBelongToUser(identifier, childDocSnap);

        const childDocRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, childId);

        tx.set(
          childDocRef,
          {
            parentDocumentId: parentDocumentId,
            updatedAt: new Date().toISOString(),
          },
          { merge: true },
        );
      }
    }

    // update parent
    if (parentDocSnap && parentDocRef) {
      const parentChildDocumentIds: string[] = parentDocSnap.data().childDocumentIds || [];
      const updatedChildDocumentIds = parentChildDocumentIds.filter((childId) => childId !== id);
      // add deleted blog's children to parent
      updatedChildDocumentIds.push(...childDocumentIds);

      tx.set(
        parentDocRef,
        {
          childDocumentIds: updatedChildDocumentIds,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
    }

    tx.delete(docRef);
  });
};

export const updateBlog = async (identifier: UniqueDocIdentifier, data: RecursivePartial<CoreBlog>): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updateData = normalizeObject(data, {
    replaceWith: null,
    dropKeys: true,
  });

  const updatedData = {
    updatedAt: new Date().toISOString(),
    draft: updateData,
  } as RecursivePartial<FBlog>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const resetBlog = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const resetData = {
    draft: null,
  } as RecursivePartial<FBlog>;

  await setDoc(docRef, resetData, { merge: true });
};

export const publishBlog = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    revision: docSnap.data().revision + 1,
    updatedAt: new Date().toISOString(),
    publishedAt: new Date().toISOString(),
    latest: docSnap.data().draft,
    draft: null,
  } as RecursivePartial<FBlog>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const setBlogVisibility = async (
  identifier: UniqueDocIdentifier,
  visibility: BlogMetadata["visibility"],
): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.DOCUMENTS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    visibility,
  } as RecursivePartial<FBlog>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const getBreadcrumbs = async (
  identifier: UniqueDocIdentifier,
): Promise<IGetBreadcrumbResponse["breadcrumbs"]> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const res = await httpsCallable<IGetBreadcrumbRequest, IGetBreadcrumbResponse>(
    functions,
    "IGBreadcrumbs", // data:image/webp;base64,<base64-data>
  )({
    blogId: id,
  });

  return res.data.breadcrumbs;
};
